import React, { useState, useEffect } from "react";
import { SupervisorMap } from "../types/SupervisorMap";
import { DriverSuperUpdatePayload } from "../types/DriverSuperUpdatePayload";
import { DriverSuperCreatePayload } from "../types/DriverSuperCreatePayload";
import { Card } from "src/components/card";
import useAccountEnhanced from "../../../hooks/use-account-enhanced";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { hrisConfigMutations } from "src/api/mvte/hris-rep-query-options";
import { hrisConfigQueryOptions } from "src/api/mvte/hris-rep-query-options";
import { Supervisor } from "../types/Supervisor";

type SortKey = "supervisor" | "modified";
type SortDir = "asc" | "desc";

function DriverSupervisorMap()
{
    const driverSupervisorMap = useQuery({ ...hrisConfigQueryOptions.hrDriverSupervisorMapQueryOptions() });
    const driverSupervisors = useQuery({ ...hrisConfigQueryOptions.hrSupervisorQueryOptions() });
    const driverCreateMutation = hrisConfigMutations.useCreateDriverSupervisorMap();
    const driverUpdateMutation = hrisConfigMutations.useUpdateDriverSupervisorMap();
    const [sorts, setSorts] = useState<{ key: SortKey; dir: SortDir }[]>([
        { key: "modified", dir: "asc" },
    ]);
    const [searchTerm, setSearchTerm] = useState("");
    const accountEnhanced = useAccountEnhanced();
    const [data, setData] = useState<SupervisorMap[]>([]);
    const [editingId, setEditingId] = useState<{FleetSupervisorCode: string, SupervisorUid: number} | null>(null);
    const [editValues, setEditValues] = useState<
    {
        SupervisorName: string;
        SupervisorUID: number;
    }>({ SupervisorName: "", SupervisorUID: 0 });
    const [isCreating, setIsCreating] = useState(false);
    const ASSIGNEE_OPTIONS: Supervisor[] = (driverSupervisors.data ?? []).map((a) => 
    ({
        EmployeeUid: a.EmployeeUid,
        FirstName: `${a.FirstName}`,
        LastName: `${a.LastName}`,
    }));
    const [newRowValues, setNewRowValues] = useState<
    {
        FleetCode: string;
        SupervisorName: string;
        SupervisorUID: number;
    }>
    ({
        FleetCode: "",
        SupervisorName: "",
        SupervisorUID: 0,
    });
    const [columnFilters, setColumnFilters] = useState({
        FleetSupervisorCode: "",
        Supervisor: "",
    });
    const filteredData = data.filter((supervisor) => 
    {
        const fullName = `${supervisor.FirstName} ${supervisor.LastName}`.toLowerCase();
        const fleetCode = supervisor.FleetSupervisorCode.toLowerCase();
        const createdDate = dayjs(supervisor.CreatedAtUtc).format("MM/D/YYYY h:mm A").toLowerCase();
        const modifiedDate = dayjs(supervisor.UpdatedAtUtc).format("MM/D/YYYY h:mm A").toLowerCase();
        if (searchTerm.trim() !== "") {
            const term = searchTerm.toLowerCase();
            const matchesMain =
                fullName.includes(term) || fleetCode.includes(term) ||
                createdDate.includes(term) || modifiedDate.includes(term);
            if (!matchesMain) return false;
        }
        if (columnFilters.FleetSupervisorCode.trim() !== "" && !fleetCode.includes(columnFilters.FleetSupervisorCode.toLowerCase())) return false;
        if (columnFilters.Supervisor.trim() !== "" && !fullName.includes(columnFilters.Supervisor.toLowerCase())) return false;
        return true;
    });
    const isLoading = driverSupervisorMap.isLoading

    useEffect(() => 
    {
        if (driverSupervisorMap.data) 
        {
            setData(driverSupervisorMap.data);
        }
    }, [driverSupervisorMap.data]);

    function toggleSort(key: SortKey)
    {
        setSorts((prev) =>
        {
            const existing = prev.find((s) => s.key === key);
            if (!existing) return [...prev, { key, dir: "asc" }];
            if (existing.dir === "asc") return prev.map((s) => s.key === key ? { ...s, dir: "desc" } : s);
            return prev.filter((s) => s.key !== key);
        });
    }

    function sortLabel(key: SortKey)
    {
        const i = sorts.findIndex((s) => s.key === key);
        if (i === -1) return "↕";
        const arrow = sorts[i].dir === "asc" ? "▲" : "▼";
        return sorts.length > 1 ? `${arrow}${i + 1}` : arrow;
    }

    const sortData = sorts.length === 0 ? filteredData : [...filteredData].sort((a, b) =>
    {
        for (const { key, dir } of sorts)
        {
            const diff = key === "modified"
                ? dayjs(a.UpdatedAtUtc).valueOf() - dayjs(b.UpdatedAtUtc).valueOf()
                : `${a.FirstName} ${a.LastName}`.localeCompare(`${b.FirstName} ${b.LastName}`, undefined, { sensitivity: "base" });
            if (diff !== 0) return dir === "asc" ? diff : -diff;
        }
        return 0;
    });

    function findSupervisorByName(name: string)
    {
        return ASSIGNEE_OPTIONS.find((o) => `${o.FirstName} ${o.LastName}` === name);
    }

    function handleEdit(rep: SupervisorMap)
    {
        setEditingId({FleetSupervisorCode: rep.FleetSupervisorCode, SupervisorUid: rep.SupervisorUid});
        setEditValues(
        {
            SupervisorName: `${rep.FirstName} ${rep.LastName}`,
            SupervisorUID: rep.SupervisorUid,
        });
    }
    async function handleConfirm()
    {
        if (editingId === null) return;
        if (editValues.SupervisorUID === 0) return;

        const supervisor = data.find((r) => 
            r.FleetSupervisorCode === editingId.FleetSupervisorCode &&
            r.SupervisorUid === editingId.SupervisorUid
        );
        if (!supervisor) return;

        const noChanges = 
            editValues.SupervisorUID === supervisor.SupervisorUid;

        if (noChanges)
        {
            setEditingId(null);
            return;
        }
        const updatePayload: DriverSuperUpdatePayload =
        {
            FleetSupervisorCode: supervisor.FleetSupervisorCode,
            NewSupervisorEmployeeUID: editValues.SupervisorUID,
            SourceSystem: "MVTE-Center",
            ChangedBy: accountEnhanced.me.employeeCode,
            Note: "Updating driver supervisor mapping as requested on ticket #",
        };
        await driverUpdateMutation.mutateAsync({ payload: updatePayload });
        setData((prev) => prev.map((r) => 
            r.FleetSupervisorCode === editingId.FleetSupervisorCode &&
            r.SupervisorUid === editingId.SupervisorUid ?
        {
            ...r,
            SupervisorUid: editValues.SupervisorUID,
            FirstName: editValues.SupervisorName.split(" ")[0] ?? "",
            LastName: editValues.SupervisorName.split(" ").slice(1).join(" "),
            UpdatedAtUtc: new Date().toISOString(),
        } : r));
        setEditingId(null);
    }
    function handleCancel()
    {
        setEditingId(null);
    }
    function handleCreateOpen()
    {
        setIsCreating(true);
        setNewRowValues(
        {
            FleetCode: "",
            SupervisorName: (ASSIGNEE_OPTIONS[0]?.FirstName + " " + ASSIGNEE_OPTIONS[0]?.LastName),
            SupervisorUID: ASSIGNEE_OPTIONS[0]?.EmployeeUid ?? 0,
        });
        setEditingId(null);
    }
    function handleCreateCancel()
    {
        setIsCreating(false);
    }
    async function handleCreateSave()
    {
        if (!newRowValues.FleetCode.trim()) return;
        if (newRowValues.SupervisorUID === 0) return;
        const createPayload: DriverSuperCreatePayload =
        {
            FleetSupervisorCode: newRowValues.FleetCode.trim(),
            SupervisorUID: newRowValues.SupervisorUID,
            SourceSystem: "MVTE-Center",
            ChangedBy: accountEnhanced.me.employeeCode,
            Note: "Creating driver supervisor map as requested on ticket #",
        };
        await driverCreateMutation.mutateAsync({ payload: createPayload });
        await driverSupervisorMap.refetch();
        setIsCreating(false);
    }
    if (isLoading)
    {
        return (
            <Card>
                <div style={{ padding: "24px", textAlign: "center", color: "#555", fontSize: "15px" }}>
                    Loading Supervisor Map...
                </div>
            </Card>
        );
    }
    const canSaveNew = newRowValues.FleetCode.trim() !== "" && newRowValues.SupervisorUID !== 0;

    return (
        <Card>
            <div style={{ padding: "24px" }}>
                <datalist id="supervisor-options">
                    {
                        ASSIGNEE_OPTIONS.map((o) =>
                        (
                            <option key={o.EmployeeUid} value={`${o.FirstName} ${o.LastName}`} />
                        ))
                    }
                </datalist>

            {/* ── Toolbar ── */}
                <div style={toolbarStyle}>
                    <input 
                        type="text"
                        placeholder="Search by fleet code or supervisor..."
                        value={searchTerm}
                        onChange= {(e) => setSearchTerm(e.target.value)}
                        style={searchInputStyle}/>
                    <button
                        onClick={handleCreateOpen}
                        disabled={isCreating}
                        style={isCreating ? createButtonDisabledStyle : createButtonStyle}>
                        + New Mapping
                    </button>
                </div>

                <table style={tableStyle}>
                    <thead>
                        <tr style={{ backgroundColor: "#f0f0f0" }}>
                            <th style={thStyle}>Fleet Code</th>
                            <th style={thStyle}>
                                Supervisor {"  "}
                                <button onClick={() => toggleSort("supervisor")} title="Toggle A→Z / Z→A / off" style={sortButtonStyle}>
                                    {sortLabel("supervisor")}
                                </button>
                            </th>
                            <th style={thStyle}>Created Date</th>
                            <th style={thStyle}>
                                Modified Date {"  "}
                                <button onClick={() => toggleSort("modified")} title="Toggle ASC / DESC / off by Modified Date" style={sortButtonStyle}>
                                    {sortLabel("modified")}
                                </button>
                            </th>
                            <th style={{ ...thStyle, width: "160px" }}>Actions</th>
                        </tr>
                        <tr style={{ backgroundColor: "#f7f7f7" }}>
                            <th style={thStyle}>
                            <input type="text" placeholder="Filter..." value={columnFilters.FleetSupervisorCode}
                                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, FleetSupervisorCode: e.target.value }))}
                                    style={columnSearchInputStyle} />
                            </th>
                            <th style={thStyle}>
                            <input type="text" placeholder="Filter..." value={columnFilters.Supervisor}
                                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, Supervisor: e.target.value }))}
                                    style={columnSearchInputStyle} />
                            </th>
                            <th style={{ ...thStyle, width: "160px" }}></th>
                            <th style={{ ...thStyle, width: "160px" }}></th>
                            <th style={{ ...thStyle, width: "160px" }}></th>
                            </tr>
                    </thead>

                    <tbody>
                    {/* ── New-row entry ── */}
                        {
                            isCreating && 
                            (
                                <tr style={{ backgroundColor: "#f9fbe7" }}>
                                    <td style={tdStyle}>
                                        <input
                                        type="text"
                                        placeholder="Fleet code"
                                        value={newRowValues.FleetCode}
                                        onChange={(e) => setNewRowValues((prev) => ({ ...prev, FleetCode: e.target.value }))}
                                        style={newRowInputStyle}
                                        autoFocus/>
                                    </td>
                                    <td style={tdStyle}>
                                        <input
                                        list="supervisor-options"
                                        placeholder="Search supervisor..."
                                        value={newRowValues.SupervisorName}
                                        onFocus={(e) => e.target.select()}
                                        onChange={(e) =>
                                        {
                                            const name = e.target.value;
                                            const match = findSupervisorByName(name);
                                            setNewRowValues((prev) => ({
                                                ...prev,
                                                SupervisorName: name,
                                                SupervisorUID: match?.EmployeeUid ?? 0,
                                            }));
                                        }}
                                        style={newRowInputStyle} />
                                    </td>
                                    <td style={{ ...tdStyle, color: "#bbb" }}>—</td>
                                    <td style={{ ...tdStyle, color: "#bbb" }}>—</td>
                                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                                        <button
                                        onClick={handleCreateSave}
                                        disabled={!canSaveNew}
                                        style={canSaveNew ? confirmButtonStyle : { ...confirmButtonStyle, opacity: 0.5, cursor: "not-allowed"}}>
                                            Save
                                        </button>
                                        <button onClick={handleCreateCancel} style={cancelButtonStyle}>
                                            Cancel
                                        </button>
                                    </td>
                                </tr>
                            )
                        }

                        {/* ── Existing rows ── */}
                        {
                            sortData.map((supervisor) =>
                            {
                                const isEditing = editingId !== null && editingId?.FleetSupervisorCode === supervisor.FleetSupervisorCode && editingId.SupervisorUid === supervisor.SupervisorUid;
                                const canConfirm = editValues.SupervisorUID !== 0;
                                return (
                                    <tr key={`${supervisor.FleetSupervisorCode}-${supervisor.SupervisorUid}`}>
                                        <td style={tdStyle}>{supervisor.FleetSupervisorCode}</td>
                                        <td style={tdStyle}>
                                        {
                                            isEditing ?
                                            (
                                                <input
                                                list="supervisor-options"
                                                placeholder="Search supervisor..."
                                                value={editValues.SupervisorName}
                                                onFocus={(e) => e.target.select()}
                                                onChange={(e) =>
                                                {
                                                    const name = e.target.value;
                                                    const match = findSupervisorByName(name);
                                                    setEditValues({
                                                        SupervisorName: name,
                                                        SupervisorUID: match?.EmployeeUid ?? 0,
                                                    });
                                                }}
                                                style={newRowInputStyle} />
                                            ) : (`${supervisor.FirstName} ${supervisor.LastName}`)
                                        }
                                        </td>
                                        <td style={tdStyle}>
                                            <span title={supervisor.CreatedAtUtc} style={{ cursor: "default" }}>
                                            {
                                                dayjs(supervisor.CreatedAtUtc).format("MM/D/YYYY h:mm A")
                                            }
                                            </span>
                                        </td>
                                        <td style={tdStyle}>
                                            <span title={supervisor.UpdatedAtUtc} style={{ cursor: "default" }}>
                                            {
                                                dayjs(supervisor.UpdatedAtUtc).format("MM/D/YYYY h:mm A")
                                            }
                                            </span>
                                        </td>
                                        <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                                        {
                                            isEditing ?
                                            (
                                                <>
                                                    <button
                                                    onClick={handleConfirm}
                                                    disabled={!canConfirm}
                                                    style={canConfirm ? confirmButtonStyle : { ...confirmButtonStyle, opacity: 0.5, cursor: "not-allowed" }}>
                                                        Confirm
                                                    </button>
                                                    <button onClick={handleCancel} style={cancelButtonStyle}>
                                                        Cancel
                                                    </button>
                                                </>
                                            ) : (<button onClick={() => handleEdit(supervisor)} style={editButtonStyle}>Edit</button>)
                                        }
                                        </td>
                                    </tr>
                                );
                            })
                        }
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

const tableStyle: React.CSSProperties = 
{
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "auto",
};
const thStyle: React.CSSProperties = 
{
    border: "1px solid #ccc",
    padding: "10px 12px",
    textAlign: "center",
    fontWeight: 600,
};
const tdStyle: React.CSSProperties = 
{
    border: "1px solid #ccc",
    padding: "10px 12px",
    textAlign: "center",
    fontSize: "14px",
    backgroundColor: "white"
};
const editButtonStyle: React.CSSProperties = 
{
    padding: "4px 12px",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#0078d4",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
};
const confirmButtonStyle: React.CSSProperties = 
{
    padding: "4px 10px",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#2e7d32",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    marginRight: "6px",
};
const cancelButtonStyle: React.CSSProperties = 
{
    padding: "4px 10px",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#999",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
};
const toolbarStyle: React.CSSProperties = 
{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
};
const createButtonStyle: React.CSSProperties = 
{
    padding: "6px 14px",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#0078d4",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    fontWeight: 600,
};
const createButtonDisabledStyle: React.CSSProperties = 
{
    ...createButtonStyle,
    opacity: 0.5,
    cursor: "not-allowed",
};
const newRowInputStyle: React.CSSProperties = 
{
    width: "100%",
    padding: "4px 6px",
    fontSize: "14px",
    border: "1px solid #aaa",
    borderRadius: "4px",
    boxSizing: "border-box",
};
const searchInputStyle: React.CSSProperties = 
{
    flex: 1,
    maxWidth: "320px",
    padding: "6px 10px",
    fontSize: "13px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    outline: "none",
    color: "#222",
}
const sortButtonStyle: React.CSSProperties = 
{
    padding: "0.6px",
    fontSize: "12px",
    cursor: "pointer",
    backgroundColor: "transparent",
    border: "1px solid #aaa",
    borderRadius: "4px",
    lineHeight: "1.6",
}
const columnSearchInputStyle: React.CSSProperties = 
{
    width: "100%",
    padding: "4px 6px",
    fontSize: "12px",
    fontWeight: 400,
    border: "1px solid #ccc",
    borderRadius: "4px",
    outline: "none",
    boxSizing: "border-box",
    color: "#222",
};
export { DriverSupervisorMap };